import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isSeatDetailsAvailable } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { SeatAvailabilityStatus, SeatType } from '@vista-digital/ocapi-types/v1';
import { memo, useContext, useRef } from 'react';
import { useOccContext } from '../../../context/occ/hooks/use-occ-context';
import { Icon } from '../../../subcomponents/icon/icon';
import { getKeyCodeForEvent, KeyCode } from '../../../utils/key-code';
import { getSeatRectangle } from '../positioning/seat';
import { SeatPickerContextInstance } from '../types/context';
import { getAppearanceForSeat } from '../utils/get-appearance-for-seat';
import { getAriaLabelForSeat } from '../utils/get-aria-label-for-seat';
import { getClassForSeat } from '../utils/get-class-for-seat';
const b = bemClassBuilder('v-seat-picker-seat');
export const SeatPickerSeat = memo(props => {
    var _a;
    const { accessibilityText, i18n, displayText: { seat: seatDisplayText } } = useOccContext();
    const { seat, availabilityStatus, isSelectable, isSelected, isFriend, area, scaleFactor } = props;
    const { config, onSeatClicked, seatIdToAppearanceRule } = useContext(SeatPickerContextInstance);
    const seatDetails = { seat, availabilityStatus, isSelected, isSelectable, isFriend };
    const seatType = seat.type;
    const isAvailable = isSeatDetailsAvailable(seatDetails);
    const { label: seatLabel } = seat;
    const { x, y, width, height } = getSeatRectangle(seatType, seat.position, area, scaleFactor, config);
    const svgIconSize = Math.min(width, height);
    const accessibilityProps = {
        role: 'button',
        tabIndex: isAvailable ? 0 : -1,
        'aria-label': getAriaLabelForSeat(seat, isAvailable, accessibilityText, seatDisplayText, i18n),
        'aria-pressed': isSelected
    };
    const seatRef = useRef(null);
    const selectSeat = async (resetFocus) => {
        var _a;
        await onSeatClicked(seatDetails);
        // Remove focus after the seat has been clicked.
        if (resetFocus)
            return;
        (_a = seatRef.current) === null || _a === void 0 ? void 0 : _a.blur();
    };
    const handleKeyPress = event => {
        const keyCode = getKeyCodeForEvent(event);
        if (keyCode === KeyCode.Enter || keyCode === KeyCode.Space) {
            selectSeat(false);
        }
    };
    const handleClick = () => {
        selectSeat(true);
    };
    const handleMouseDown = event => {
        // Prevent default so the seat doesn't become focused.
        event.preventDefault();
    };
    const isHouse = availabilityStatus === SeatAvailabilityStatus.House;
    const customisations = (_a = seatIdToAppearanceRule.get(seat.id)) === null || _a === void 0 ? void 0 : _a.customisations;
    const customClass = customisations === null || customisations === void 0 ? void 0 : customisations.className;
    const seatAppearance = getAppearanceForSeat(seatType, isAvailable, isSelected, isFriend, isHouse, config.seats.appearance, customisations === null || customisations === void 0 ? void 0 : customisations.appearance);
    const showLabel = seatAppearance.showLabels && seatLabel && seatLabel.length > 0;
    let iconX = (width - svgIconSize) / 2;
    let labelX = width / 2 + (seatAppearance.labelOffsetX || 0);
    if (config.seats.legacyMode) {
        iconX = legacyIconX(iconX, seat.type);
        labelX = legacyLabelX(labelX, seat.type, svgIconSize, seatAppearance.labelOffsetX);
    }
    return (_jsx("a", Object.assign({ className: getClassForSeat(b, customClass, seat.type, isAvailable, isSelected, isFriend, isHouse) }, accessibilityProps, { onClick: handleClick, onMouseDown: handleMouseDown, onKeyPress: handleKeyPress, ref: seatRef }, { children: _jsxs("svg", Object.assign({ x: x, y: y, width: width, height: height }, { children: [_jsx("rect", { className: b.element('background'), x: "0", y: "0", width: width, height: height }), _jsx(Icon, { name: seatAppearance.iconName, className: b.element('icon'), size: svgIconSize, x: iconX, y: (height - svgIconSize) / 2 }), showLabel && (_jsx("text", Object.assign({ className: b.element('label'), x: labelX, y: height / 2 + (seatAppearance.labelOffsetY || 0), dominantBaseline: "central", alignmentBaseline: "central", textAnchor: "middle" }, { children: seatLabel })))] })) })));
});
SeatPickerSeat.displayName = 'SeatPickerSeat';
/**
 * To better display seat icons when dealing with legacy seat layouts.
 * A specific example is non square seats causing sofa icons to be separated.
 */
export function legacyIconX(centeredIconX, seatType) {
    switch (seatType) {
        case SeatType.SofaRight:
            return 0; // Align the right sofa to the left side
        case SeatType.SofaLeft:
            return centeredIconX * 2; // Align the left sofa to the right side
        default:
            return centeredIconX;
    }
}
export function legacyLabelX(centeredLabelX, seatType, iconWidth, offset = 0) {
    const halfIconWidth = iconWidth / 2;
    switch (seatType) {
        case SeatType.SofaRight:
            return halfIconWidth + offset;
        case SeatType.SofaLeft:
            return centeredLabelX * 2 - halfIconWidth - offset;
        default:
            return centeredLabelX;
    }
}
